<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Button as UiButton, LoadingButton } from '@/components/ui/button'
import Recommended from '../RecommendedSection.vue'
import Heading from '../Heading.vue'
import LoginForm from './BaseLoginForm.vue'
import { ref } from 'vue'

import { useRouter } from 'vue-router'
const router = useRouter()

const props = defineProps<{
  email: string
  onSendCode: (email: string) => Promise<void>
  onEmailChange: (email: string) => void
  onKeepPracticing: () => void
  onContinueWithoutSaving?: () => void
  isCodeSent: boolean
  countdown: number
  errors: Record<string, string>
  subTitle?: string
}>()

const isLoading = ref(false)

const handleContinueWithoutSaving = () => {
  props.onContinueWithoutSaving?.()
  router.push({ name: 'home2' })
}

const handleSendCode = async (email: string) => {
  isLoading.value = true
  try {
    await props.onSendCode(email)
  } catch (error) {
    console.error('Error sending code:', error)
  } finally {
    isLoading.value = false
  }
}

const { t } = useI18n()
</script>

<template>
  <div class="w-full max-w-[400px] pb-10">
    <Heading
      :title="t('LoginPopup.label.ContinueWithEmail')"
      :description="t('LoginPopup.label.TakingABreak')"
      class="pt-16 lg:pt-20"
    />

    <Recommended class="mb-6 mt-8" title="Recommended for best experience" />

    <LoginForm
      :email="email"
      :onEmailChange="onEmailChange"
      :isCodeSent="isCodeSent"
      :countdown="countdown"
      :errors="errors"
      @submit="handleSendCode"
    >
      <template #buttons="{ disabled }">
        <div class="flex-1">
          <ui-button
            @click="props.onKeepPracticing"
            class="h-[48px] w-full cursor-pointer rounded-[8px] border-[0.5px] border-white/10 bg-[#19233A] text-sm text-white/70"
            type="button"
          >
            {{ t('LoginPopup.button.KeepPracticing') }}
          </ui-button>
        </div>
        <div
          class="h-[48px] flex-1 cursor-pointer rounded-md bg-gradient-to-r from-[#005EDA]/50 to-[#F3004E]/50 p-[0.5px] hover:opacity-[0.9]"
        >
          <LoadingButton
            variant="loginForm"
            :disabled="disabled"
            :loading="isLoading"
            class="h-[47px] w-full cursor-pointer rounded-md bg-gradient-to-r from-[#005EDA] to-[#F3004E] text-sm text-white/70"
            type="submit"
          >
            {{ t('LoginPopup.button.SendCode') }}
          </LoadingButton>
        </div>
      </template>

      <template #footer>
        <div class="mt-4 text-center" v-if="props.onContinueWithoutSaving">
          <ui-button
            @click="handleContinueWithoutSaving"
            class="bg-transparent text-sm text-text-helper hover:text-white"
          >
            {{ t('LoginPopup.button.ContinueWithoutSaving') }}
          </ui-button>
        </div>
      </template>
    </LoginForm>
  </div>
</template>
